import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { CoordinateTransformer } from "../coordinateTransformer";
import { CalibrationPoint, Coordinate, DevicePin } from "../types";
import { colorForStatus, colorForType, labelForType } from "../colors";

interface SiteLayoutCanvasProps {
  imageUrl: string;
  calibrationPoints: CalibrationPoint[];
  devices: DevicePin[];
  isCalibrated: boolean;
  transformer: CoordinateTransformer | null;
  /** When true, clicks are forwarded to onImageClick (calibration / device placement). */
  isAddingMode?: boolean;
  onImageClick?: (coord: Coordinate) => void;
  /** Colour device markers by alertStatus instead of by device type (overview mode). */
  colorByStatus?: boolean;
  height?: number;
}

interface ImageSize {
  width: number;
  height: number;
}

const PADDING = 24;

const SiteLayoutCanvas: React.FC<SiteLayoutCanvasProps> = ({
  imageUrl,
  calibrationPoints,
  devices,
  isCalibrated,
  transformer,
  isAddingMode = false,
  onImageClick,
  colorByStatus = false,
  height = 520,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const [imageSize, setImageSize] = useState<ImageSize | null>(null);

  const [zoom, setZoom] = useState(1);
  const [panOffset, setPanOffset] = useState<Coordinate>({ x: 0, y: 0 });
  const [markerOpacity, setMarkerOpacity] = useState(0.8);
  const [showGrid, setShowGrid] = useState(true);
  const [hoverCoordinate, setHoverCoordinate] = useState<{
    northing: number;
    easting: number;
  } | null>(null);

  const isDraggingRef = useRef(false);
  const dragStartRef = useRef<Coordinate>({ x: 0, y: 0 });
  const movedRef = useRef(false);
  const defaultPanRef = useRef<Coordinate>({ x: 0, y: 0 });

  // Load image
  useEffect(() => {
    if (!imageUrl) {
      imageRef.current = null;
      setImageSize(null);
      return;
    }
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      imageRef.current = img;
      setImageSize({ width: img.naturalWidth, height: img.naturalHeight });
      // Fit-to-canvas reset
      setZoom(1);
      setPanOffset({ x: 0, y: 0 });
    };
    img.onerror = () => {
      imageRef.current = null;
      setImageSize(null);
    };
    img.src = imageUrl;
  }, [imageUrl]);

  /** Scale factor so the natural image fits inside the canvas at zoom 1. */
  const getBaseScale = useCallback(
    (canvas: HTMLCanvasElement): number => {
      if (!imageSize) return 1;
      return Math.min(
        canvas.width / imageSize.width,
        canvas.height / imageSize.height,
      );
    },
    [imageSize],
  );

  /** Convert a canvas (CSS px) point to image-natural coordinates (uncalibrated mode). */
  const canvasToImage = useCallback(
    (canvas: HTMLCanvasElement, cx: number, cy: number): Coordinate => {
      const base = getBaseScale(canvas);
      const scale = base * zoom;
      const drawW = (imageSize?.width ?? 0) * scale;
      const drawH = (imageSize?.height ?? 0) * scale;
      const originX = (canvas.width - drawW) / 2 + panOffset.x;
      const originY = (canvas.height - drawH) / 2 + panOffset.y;
      return {
        x: (cx - originX) / scale,
        y: (cy - originY) / scale,
      };
    },
    [getBaseScale, zoom, panOffset, imageSize],
  );

  // Real-world bounding box (calibrated mode) — image corners + device positions
  const rwCorners = useMemo(() => {
    if (!imageSize || !transformer || !isCalibrated) return null;
    const corners = [
      { x: 0, y: 0 },
      { x: imageSize.width, y: 0 },
      { x: 0, y: imageSize.height },
      { x: imageSize.width, y: imageSize.height },
    ].map((c) => transformer.imageToRealWorld(c));
    let minE = Math.min(...corners.map((r) => r.easting));
    let maxE = Math.max(...corners.map((r) => r.easting));
    let minN = Math.min(...corners.map((r) => r.northing));
    let maxN = Math.max(...corners.map((r) => r.northing));
    devices.forEach((d) => {
      minE = Math.min(minE, d.realWorld.easting);
      maxE = Math.max(maxE, d.realWorld.easting);
      minN = Math.min(minN, d.realWorld.northing);
      maxN = Math.max(maxN, d.realWorld.northing);
    });
    return { minE, maxE, minN, maxN };
  }, [imageSize, transformer, isCalibrated, devices]);

  // Centre the view whenever we enter calibrated mode or the content changes
  useEffect(() => {
    if (!rwCorners) return;
    const canvas = canvasRef.current;
    if (!canvas || canvas.width === 0) return;
    const spanE = rwCorners.maxE - rwCorners.minE || 1;
    const spanN = rwCorners.maxN - rwCorners.minN || 1;
    const base = Math.min(
      (canvas.width - 2 * PADDING) / spanE,
      (canvas.height - 2 * PADDING) / spanN,
    );
    const centreX = (canvas.width - spanE * base) / 2;
    const centreY = (canvas.height - spanN * base) / 2;
    defaultPanRef.current = { x: centreX, y: centreY };
    setZoom(1);
    setPanOffset({ x: centreX, y: centreY });
  }, [rwCorners]);

  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const img = imageRef.current;
    if (!img || !imageSize) return;

    // ── CALIBRATED MODE: Easting = +x (right), Northing = +y (up) ────────────
    if (isCalibrated && transformer && rwCorners) {
      const { minE, maxE, minN, maxN } = rwCorners;
      const spanE = maxE - minE || 1;
      const spanN = maxN - minN || 1;
      const base = Math.min(
        (canvas.width - 2 * PADDING) / spanE,
        (canvas.height - 2 * PADDING) / spanN,
      );
      const S = base * zoom;

      // Unified formula (same as OverlayCanvas):
      //   screen_x = (e - minE) * S + panOffset.x
      //   screen_y = (maxN - n) * S + panOffset.y
      const rw2c = (e: number, n: number): Coordinate => ({
        x: (e - minE) * S + panOffset.x,
        y: (maxN - n) * S + panOffset.y,
      });

      // 1. Image via affine setTransform
      const { a, b, tx, ty } = transformer.getParams();
      const det = a * a + b * b;
      if (det !== 0) {
        ctx.save();
        ctx.globalAlpha = markerOpacity;
        ctx.setTransform(
          /* m11 */ (S * a) / det,
          /* m12 */ (-S * b) / det,
          /* m21 */ (S * b) / det,
          /* m22 */ (S * a) / det,
          /* dx  */ S * (-(a * tx + b * ty) / det - minE) + panOffset.x,
          /* dy  */ S * (maxN + (b * tx - a * ty) / det) + panOffset.y,
        );
        ctx.drawImage(img, 0, 0);
        ctx.restore();
        ctx.setTransform(1, 0, 0, 1, 0, 0);
      }

      // 2. HK1980 grid (E lines vertical, N lines horizontal)
      if (showGrid) {
        const maxSpan = Math.max(spanE, spanN);
        if (maxSpan > 0) {
          const rawStep = maxSpan / 8;
          const mag = Math.pow(10, Math.floor(Math.log10(rawStep)));
          const residual = rawStep / mag;
          const nice =
            residual <= 1 ? 1 : residual <= 2 ? 2 : residual <= 5 ? 5 : 10;
          const gridStep = nice * mag;

          ctx.save();
          ctx.strokeStyle = "rgba(13, 110, 253, 0.35)";
          ctx.fillStyle = "rgba(13, 110, 253, 0.9)";
          ctx.lineWidth = 1;
          ctx.font = "10px sans-serif";

          const startE = Math.ceil(minE / gridStep) * gridStep;
          for (let e = startE; e <= maxE; e += gridStep) {
            const p1 = rw2c(e, minN);
            const p2 = rw2c(e, maxN);
            ctx.beginPath();
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.stroke();
            ctx.fillText(`E ${Math.round(e)}`, p1.x + 2, p1.y - 2);
          }
          const startN = Math.ceil(minN / gridStep) * gridStep;
          for (let n = startN; n <= maxN; n += gridStep) {
            const p1 = rw2c(minE, n);
            const p2 = rw2c(maxE, n);
            ctx.beginPath();
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.stroke();
            ctx.fillText(`N ${Math.round(n)}`, p1.x + 2, p1.y - 2);
          }
          ctx.restore();
        }
      }

      // 3. Device markers
      devices.forEach((d) => {
        const p = rw2c(d.realWorld.easting, d.realWorld.northing);
        const color = colorByStatus
          ? colorForStatus(d.alertStatus)
          : colorForType(d.type);
        ctx.save();
        ctx.fillStyle = color;
        ctx.strokeStyle = "#fff";
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(p.x, p.y, 6, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        ctx.restore();

        ctx.save();
        ctx.font = "12px sans-serif";
        ctx.fillStyle = "#000";
        ctx.strokeStyle = "#fff";
        ctx.lineWidth = 3;
        ctx.strokeText(d.name, p.x + 10, p.y + 4);
        ctx.fillText(d.name, p.x + 10, p.y + 4);
        ctx.restore();
      });

      return; // calibrated drawing complete
    }

    // ── UNCALIBRATED MODE: image-pixel space ──────────────────────────────────
    const base = getBaseScale(canvas);
    const scale = base * zoom;
    const drawW = imageSize.width * scale;
    const drawH = imageSize.height * scale;
    const originX = (canvas.width - drawW) / 2 + panOffset.x;
    const originY = (canvas.height - drawH) / 2 + panOffset.y;

    const toCanvas = (p: Coordinate): Coordinate => ({
      x: originX + p.x * scale,
      y: originY + p.y * scale,
    });

    // 1. Image
    ctx.save();
    ctx.globalAlpha = markerOpacity;
    ctx.drawImage(img, originX, originY, drawW, drawH);
    ctx.restore();

    // 2. Calibration points
    calibrationPoints.forEach((cp, i) => {
      const p = toCanvas(cp.imageCoordinate);
      ctx.save();
      ctx.fillStyle = "#dc3545";
      ctx.strokeStyle = "#fff";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(p.x, p.y, 7, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = "#fff";
      ctx.font = "bold 10px sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(String(i + 1), p.x, p.y);
      ctx.restore();
    });
  }, [
    imageSize,
    getBaseScale,
    zoom,
    panOffset,
    markerOpacity,
    showGrid,
    isCalibrated,
    transformer,
    rwCorners,
    calibrationPoints,
    devices,
    colorByStatus,
  ]);

  // Redraw on any relevant change
  useEffect(() => {
    drawCanvas();
  }, [drawCanvas]);

  // Resize canvas to container
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      canvas.width = parent.clientWidth;
      canvas.height = height;
      drawCanvas();
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, [height, drawCanvas]);

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    isDraggingRef.current = true;
    movedRef.current = false;
    dragStartRef.current = { x: e.clientX, y: e.clientY };
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const cx = e.clientX - rect.left;
    const cy = e.clientY - rect.top;

    if (isDraggingRef.current) {
      const dx = e.clientX - dragStartRef.current.x;
      const dy = e.clientY - dragStartRef.current.y;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) movedRef.current = true;
      setPanOffset((prev) => ({ x: prev.x + dx, y: prev.y + dy }));
      dragStartRef.current = { x: e.clientX, y: e.clientY };
      return;
    }

    // Hover readout
    if (isCalibrated && transformer && rwCorners) {
      const spanE = rwCorners.maxE - rwCorners.minE || 1;
      const spanN = rwCorners.maxN - rwCorners.minN || 1;
      const base = Math.min(
        (canvas.width - 2 * PADDING) / spanE,
        (canvas.height - 2 * PADDING) / spanN,
      );
      const S = base * zoom;
      const easting = (cx - panOffset.x) / S + rwCorners.minE;
      const northing = rwCorners.maxN - (cy - panOffset.y) / S;
      setHoverCoordinate({
        northing: Math.round(northing),
        easting: Math.round(easting),
      });
    } else {
      setHoverCoordinate(null);
    }
  };

  const handleMouseUp = (e: React.MouseEvent<HTMLCanvasElement>) => {
    isDraggingRef.current = false;
    if (movedRef.current) return; // was a pan, not a click
    if (!isAddingMode || !onImageClick) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const cx = e.clientX - rect.left;
    const cy = e.clientY - rect.top;

    if (isCalibrated && transformer && rwCorners) {
      // Calibrated: canvas → real-world (inverse rw2c) → image pixels
      const spanE = rwCorners.maxE - rwCorners.minE || 1;
      const spanN = rwCorners.maxN - rwCorners.minN || 1;
      const base = Math.min(
        (canvas.width - 2 * PADDING) / spanE,
        (canvas.height - 2 * PADDING) / spanN,
      );
      const S = base * zoom;
      const easting = (cx - panOffset.x) / S + rwCorners.minE;
      const northing = rwCorners.maxN - (cy - panOffset.y) / S;
      onImageClick(transformer.realWorldToImage({ easting, northing }));
    } else {
      onImageClick(canvasToImage(canvas, cx, cy));
    }
  };

  const resetView = () => {
    setZoom(1);
    if (isCalibrated && transformer) {
      setPanOffset(defaultPanRef.current);
    } else {
      setPanOffset({ x: 0, y: 0 });
    }
  };

  // Zoom-to-cursor wheel handler
  const handleWheel = useCallback(
    (e: WheelEvent) => {
      e.preventDefault();
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const mouseX = (e.clientX - rect.left) * (canvas.width / rect.width);
      const mouseY = (e.clientY - rect.top) * (canvas.height / rect.height);
      const factor = e.deltaY < 0 ? 1.1 : 0.9;
      const newZoom = Math.min(20, Math.max(0.1, zoom * factor));

      if (isCalibrated && rwCorners) {
        // Calibrated: screen_x = (e - minE) * base * zoom + panOffset.x
        // Keep content under cursor fixed: zoomPoint = (mouse - pan) / zoom
        const zoomPointX = (mouseX - panOffset.x) / zoom;
        const zoomPointY = (mouseY - panOffset.y) / zoom;
        setZoom(newZoom);
        setPanOffset({
          x: mouseX - zoomPointX * newZoom,
          y: mouseY - zoomPointY * newZoom,
        });
      } else {
        // Uncalibrated: image is centered; zoom anchor is relative to canvas centre
        const k = newZoom / zoom;
        setZoom(newZoom);
        setPanOffset((prev) => ({
          x: (mouseX - canvas.width / 2) * (1 - k) + prev.x * k,
          y: (mouseY - canvas.height / 2) * (1 - k) + prev.y * k,
        }));
      }
    },
    [zoom, panOffset, isCalibrated, rwCorners],
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.addEventListener("wheel", handleWheel, { passive: false });
    return () => canvas.removeEventListener("wheel", handleWheel);
  }, [handleWheel]);

  return (
    <div className="sl-canvas-wrap">
      <canvas
        ref={canvasRef}
        className="sl-canvas"
        style={{ cursor: isAddingMode ? "crosshair" : "grab", height }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={() => {
          isDraggingRef.current = false;
          setHoverCoordinate(null);
        }}
      />

      <div className="sl-toolbar">
        <button
          type="button"
          className="btn btn-sm"
          title="Zoom in"
          onClick={() => setZoom((z) => Math.min(5, z * 1.2))}
        >
          <i className="bi bi-zoom-in" />
        </button>
        <button
          type="button"
          className="btn btn-sm"
          title="Zoom out"
          onClick={() => setZoom((z) => Math.max(0.1, z * 0.8))}
        >
          <i className="bi bi-zoom-out" />
        </button>
        <button
          type="button"
          className="btn btn-sm"
          title="Reset view"
          onClick={resetView}
        >
          <i className="bi bi-arrows-fullscreen" />
        </button>
        <button
          type="button"
          className={`btn btn-sm ${showGrid ? "btn-primary" : ""}`}
          title="Toggle HK1980 grid"
          onClick={() => setShowGrid((g) => !g)}
          disabled={!isCalibrated}
        >
          <i className="bi bi-grid-3x3" />
        </button>
        <label className="sl-opacity" title="Layout opacity">
          <i className="bi bi-circle-half" />
          <input
            type="range"
            min={0.1}
            max={1}
            step={0.05}
            value={markerOpacity}
            onChange={(e) => setMarkerOpacity(Number(e.target.value))}
          />
        </label>
      </div>

      {hoverCoordinate && (
        <div className="sl-coord-readout">
          N {hoverCoordinate.northing} · E {hoverCoordinate.easting}
        </div>
      )}

      {isAddingMode && (
        <div className="sl-mode-banner">
          {isCalibrated
            ? "Click on the layout to place a checkpoint"
            : `Click to set calibration point ${calibrationPoints.length + 1} of 2`}
        </div>
      )}

      {/* Legend */}
      {devices.length > 0 && (
        <div className="sl-legend">
          {colorByStatus ? (
            <>
              <span>
                <i className="sl-dot" style={{ background: "#28a745" }} />{" "}
                Within bounds
              </span>
              <span>
                <i className="sl-dot" style={{ background: "#ffc107" }} /> Alert
              </span>
              <span>
                <i className="sl-dot" style={{ background: "#fd7e14" }} /> Alarm
              </span>
              <span>
                <i className="sl-dot" style={{ background: "#dc3545" }} />{" "}
                Action
              </span>
            </>
          ) : (
            Array.from(new Set(devices.map((d) => d.type))).map((t) => (
              <span key={t}>
                <i className="sl-dot" style={{ background: colorForType(t) }} />
                {labelForType(t)}
              </span>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default SiteLayoutCanvas;
